<template>
  <div class="project-management-container">
    <div class="project-inner">
      <el-card class="box-card" body-class="card-body">
        <template #header>
          <div class="card-header">
            <span class="page-title">{{ $t("message.clientProject.title") }}</span>

            <!-- Search Form -->
            <el-form :inline="true" :model="queryParams" class="search-form">
              <el-form-item :label="$t('message.device.company')">
                <el-tree-select
                  v-model="queryParams.companyId"
                  :data="companyTreeData"
                  :props="treeProps"
                  :placeholder="$t('message.device.selectCompany')"
                  check-strictly
                  clearable
                  filterable
                  style="width: 200px"
                  @change="handleSearch"
                />
              </el-form-item>

              <el-form-item :label="$t('message.clientProject.name')">
                <el-input
                  v-model="queryParams.name"
                  :placeholder="$t('message.clientProject.inputName')"
                  clearable
                  @change="handleSearch"
                  @keyup.enter="handleSearch"
                />
              </el-form-item>

              <el-form-item>
                <el-button type="primary" @click="handleSearch">
                  {{ $t("message.clientProject.search") }}
                </el-button>
                <el-button @click="resetSearch">
                  {{ $t("message.clientProject.reset") }}
                </el-button>
                <el-button v-if="canAddProject" @click="handleAdd">
                  {{ $t("message.clientProject.add") }}
                </el-button>

                <!-- ★ 视图切换按钮 -->
                <el-button-group style="margin-left: 12px;">
                  <el-button
                    :type="viewMode === 'list' ? 'primary' : 'default'"
                    @click="viewMode = 'list'"
                    :title="$t('message.common.listView')"
                  >
                    <el-icon><List /></el-icon>
                  </el-button>
                  <el-button
                    :type="viewMode === 'grid' ? 'primary' : 'default'"
                    @click="viewMode = 'grid'"
                    :title="$t('message.common.gridView')"
                  >
                    <el-icon><Grid /></el-icon>
                  </el-button>
                </el-button-group>
              </el-form-item>
            </el-form>
          </div>
        </template>

        <!-- ============ 列表模式 ============ -->
        <el-table
          v-if="viewMode === 'list'"
          :data="filteredDeviceList"
          border
          height="100%"
          style="width: 100%"
          v-loading="loading"
          :element-loading-text="$t('message.common.loading')"
        >
          <el-table-column
            prop="name"
            :label="$t('message.clientProject.name')"
            width="300"
            sortable
          />

          <el-table-column :label="$t('message.clientProject.actions')" width="240">
            <template #default="scope">
              <el-button size="small" type="primary" @click="handleViewDevice(scope.row)">
                {{ $t("message.clientProject.monitor") }}
              </el-button>
              <el-button v-if="canEditProject" size="small" @click="handleEdit(scope.row)">
                {{ $t("message.gateway.edit") }}
              </el-button>
              <el-button v-if="canDeleteProject" size="small" type="danger" @click="handleDelete(scope.row)">
                {{ $t("message.gateway.delete") }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- ============ 大图标模式 ============ -->
        <div
          v-else
          class="grid-view"
          v-loading="loading"
          :element-loading-text="$t('message.common.loading')"
        >
          <div
            v-for="item in filteredDeviceList"
            :key="item.id"
            class="grid-item"
            @dblclick="handleViewDevice(item)"
          >
            <!-- 图标区 -->
            <div class="grid-item-icon">
              <el-icon :size="56" color="#409EFF">
                <Files />
              </el-icon>
            </div>

            <!-- 名称区 -->
            <div class="grid-item-name" :title="item.name">
              {{ item.name }}
            </div>

            <!-- 悬浮操作区 -->
            <div class="grid-item-actions" @click.stop>
              <el-tooltip :content="$t('message.clientProject.monitor')" placement="top">
                <el-button size="small" type="primary" circle @click="handleViewDevice(item)">
                  <el-icon><Monitor /></el-icon>
                </el-button>
              </el-tooltip>
              <el-tooltip v-if="canEditProject" :content="$t('message.gateway.edit')" placement="top">
                <el-button size="small" circle @click="handleEdit(item)">
                  <el-icon><Edit /></el-icon>
                </el-button>
              </el-tooltip>
              <el-tooltip v-if="canDeleteProject" :content="$t('message.gateway.delete')" placement="top">
                <el-button size="small" type="danger" circle @click="handleDelete(item)">
                  <el-icon><Delete /></el-icon>
                </el-button>
              </el-tooltip>
            </div>
          </div>

          <!-- 空状态 -->
          <el-empty
            v-if="filteredDeviceList.length === 0"
            :description="$t('message.common.noData')"
            style="width: 100%"
          />
        </div>

        <!-- ★ 底部工具栏：左单选 + 右分页 -->
        <div class="bottom-toolbar">
          <!-- 左侧：动态单选组 + 滚动 -->
          <div class="radio-group-wrapper">
            <el-button
              v-if="showScrollButtons"
              @click="scrollLeft"
              class="scroll-btn scroll-left"
              size="small"
              circle
            >
              <el-icon><ArrowLeft /></el-icon>
            </el-button>

            <div class="radio-group-container" ref="radioContainer">
              <el-radio-group
                v-model="systemBussType"
                @change="handleSaveLoginTypeChange"
                size="default"
                class="business-radio-group"
              >
                <el-radio
                  v-for="option in groupsOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </el-radio>
              </el-radio-group>
            </div>

            <el-button
              v-if="showScrollButtons"
              @click="scrollRight"
              class="scroll-btn scroll-right"
              size="small"
              circle
            >
              <el-icon><ArrowRight /></el-icon>
            </el-button>
          </div>

          <!-- 右侧：分页 -->
          <div class="pagination">
            <el-pagination
              v-model:current-page="pagination.currentPage"
              v-model:page-size="pagination.pageSize"
              :total="pagination.total"
              :page-sizes="[10, 20, 30, 50,100,500]"
              layout="total, sizes, prev, pager, next, jumper"
              @current-change="handlePageChange"
              @size-change="handleSizeChange"
            />
          </div>
        </div>
      </el-card>
    </div>

    <!-- 监控弹窗 -->
    <el-dialog
      v-model="monitorDialogVisible"
      :title="monitorDialogTitle"
      :close-on-click-modal="false"
      fullscreen
      class="custom-dialog"
    >
      <template #header>
        <div class="custom-dialog-header">
          <span class="dialog-title">{{ monitorDialogTitle }}</span>
          <el-button type="primary" link @click="monitorDialogVisible = false" class="back-button">
            <el-icon><ArrowLeft /></el-icon>
            {{ t("message.common.exit") }}
          </el-button>
        </div>
      </template>
      <div class="dialog-content">
        <device-monitor
          v-if="monitorDialogVisible"
          :businessId="businessId"
          :projectId="currentProjectId"
          :projectName="currentProjectName"
        />
      </div>
    </el-dialog>

    <el-dialog
      v-model="ProjectDeailAddDialogVisible"
      :title="currentProjectTitle"
      :close-on-click-modal="false"
      fullscreen
      class="custom-dialog"
      @close="handleDialogClose"
    >
      <template #header>
        <div class="custom-dialog-header">
          <span class="dialog-title">{{ currentProjectTitle }}</span>
          <el-button type="primary" link @click="closeProjectDialog" class="back-button">
            <el-icon><ArrowLeft /></el-icon>
            {{ t("message.common.exit") }}
          </el-button>
        </div>
      </template>
      <div class="dialog-content">
        <ProjectDeailAdd
          v-if="ProjectDeailAddDialogVisible"
          :companyId="currentCompanyId"
          :businessId="businessId"
          :businessName="businessName"
          :projectId="currentProjectId"
          :projectName="currentProjectName"
          :currentProjectAddOrEdit="currentProjectAddOrEdit"
          :groupName="proGroupsOptionsName"        
          :projectSort="proProjectSort"  
          @close-dialog="closeProjectDialog"
          @save-success="handleProjectSaveSuccess"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, watch, computed, nextTick } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { useI18n } from "vue-i18n";
import DeviceMonitor from "./ClientDeviceMonitor.vue";
import ProjectDeailAdd from "./ClientProjectDeailAdd.vue";
import { projectList, projectDelete, projectGroupsQuery } from "@/api/ProjectManger";
import { getCompanyTree } from "@/api/CompanyManager";
import { useRoute } from 'vue-router';
import { usePermission } from '@/composables/usePermission'
import {
  ArrowLeft,
  ArrowRight,
  Grid,
  List,
  Files,
  Monitor,
  Edit,
  Delete
} from '@element-plus/icons-vue'

const { canAddProject, canEditProject, canDeleteProject, refresh } = usePermission()

const { t } = useI18n();

// ==================== 视图模式 ====================
const viewMode = ref(localStorage.getItem('projectViewMode') || 'list')

watch(viewMode, (val) => {
  localStorage.setItem('projectViewMode', val)
})

// 监控弹窗状态
const monitorDialogVisible = ref(false);
const monitorDialogTitle = ref("");
const currentProjectId = ref(null);
const currentProjectAddOrEdit = ref(0);
const currentCompanyId = ref(0);
const currentProjectName = ref('');
const currentProjectTitle = ref('');
const ProjectDeailAddDialogVisible = ref(false);

const route = useRoute();

const businessId = computed(() => route.query.businessId);
const businessName = computed(() => route.query.businessName);

// 状态筛选
const activeStatus = ref('all');

// 响应式数据
const deviceList = ref([]);
const loading = ref(false);

// 滚动相关
const radioContainer = ref(null)
const showScrollButtons = ref(false)

// ★ 单选组：初始用空字符串
const systemBussType = ref('')
const groupsOptions = ref([])
const groupsOptionsId = ref(0);
const groupsOptionsName = ref('');
const proGroupsOptionsName = ref('');
const proProjectSort = ref(0);

// 查询参数
const queryParams = reactive({
  companyId: null,
  businessId: 0,
  name: "",
  pageNum: 1,
  pageSize: 50,
});

const queryCompanyTreeParams = reactive({
  companyId: null,
});
const companyTreeData = ref([]);

const treeProps = {
  value: "id",
  label: "name",
  children: "children",
};

// 分页
const pagination = reactive({
  currentPage: 1,
  pageSize: 50,
  total: 0,
});

const filteredDeviceList = computed(() => {
  if (activeStatus.value === 'all') {
    return deviceList.value;
  } else if (activeStatus.value === 'online') {
    return deviceList.value.filter(project => project.state === 1);
  } else {
    return deviceList.value.filter(project => project.state === 0);
  }
});

// ==================== 滚动按钮检测 ====================
const checkScrollButtons = () => {
  nextTick(() => {
    if (!radioContainer.value) return
    const el = radioContainer.value
    showScrollButtons.value = el.scrollWidth > el.clientWidth
  })
}

// 左滚动
const scrollLeft = () => {
  if (radioContainer.value) {
    radioContainer.value.scrollBy({
      left: -150,
      behavior: 'smooth'
    })
  }
}

// 右滚动
const scrollRight = () => {
  if (radioContainer.value) {
    radioContainer.value.scrollBy({
      left: 150,
      behavior: 'smooth'
    })
  }
}

// 业务类型变化处理
const handleSaveLoginTypeChange = async(value) => {
  console.log('业务类型变化:', value)
  systemBussType.value = value
  const option = groupsOptions.value.find(opt => opt.value === value)
  groupsOptionsId.value = Number(option.value);
  groupsOptionsName.value = option.label;
  await fetchDevices();
  if (option) {
    ElMessage.success(
      t('message.clientProject.switchedToMode', { mode: option.label })
    )
  }
}

// ==================== 加载业务类型标签 ====================
const LoadGroupLabel = async () => {
  try {
    const params = {
      companyId: queryParams.companyId,
      businessId: queryParams.businessId,
      name: queryParams.name,
      pageNum: 1,
      pageSize: 10000,
    };

    const res = await projectGroupsQuery(params);
    if (!res.success) {
      ElMessage.error(res?.msg);
      return;
    }

    if (!res.data.list || res.data.list.length === 0) {
      groupsOptions.value = [];
      systemBussType.value = '';
      ElMessage.warning(t('message.ClientProjectMonitor.noBusinessData'));
      return;
    }

    groupsOptions.value = res.data.list
                                  .map(item => ({
                                    value: String(item.id),
                                    label: item.groupName
                                  }))
                                  .sort((a, b) => {
                                    // "0"（ALL）永远排最前
                                    if (a.value === '0') return -1;
                                    if (b.value === '0') return 1;
                                    // 其他按 ID 降序
                                    return Number(a.value) - Number(b.value);
                                  });
                                    
    systemBussType.value = groupsOptions.value[0].value;
    groupsOptionsId.value = Number(groupsOptions.value[0].value);
    groupsOptionsName.value = groupsOptions.value[0].label;
    console.log('业务选项加载成功，默认选中:', systemBussType.value);

    checkScrollButtons();
  } catch (error) {
    console.error('加载业务选项失败:', error);
    ElMessage.error(t('message.ClientProjectMonitor.loadBusinessFailed'));
  }
}


// el-dialog 关闭时触发（含右上角 × 和"退出"按钮）
const handleDialogClose = async () => {
  // 注意：@close 不管是通过 × 还是通过按钮关闭，都会触发一次
  // 所以这里不能直接调 closeProjectDialog，否则会重复  
  await LoadGroupLabel();
  await handleSearch();
};

// 关闭项目详情对话框
const closeProjectDialog = async() => {
  ProjectDeailAddDialogVisible.value = false; 
};

// 处理保存成功后的操作
const handleProjectSaveSuccess = async() => {  
  ProjectDeailAddDialogVisible.value = false;  
};

const handleEdit = (row) => {
  currentProjectAddOrEdit.value = 2;
  currentProjectId.value = row.id;
  currentCompanyId.value = row.company_id;
  currentProjectName.value = row.name;
  proGroupsOptionsName.value = row.groupName;
  proProjectSort.value = row.projectSort;
  const editText = t('message.common.edit');
  currentProjectTitle.value = `${row.name} - ${editText}`
  ProjectDeailAddDialogVisible.value = true;
};

const handleDelete = (row) => {
  ElMessageBox.confirm(
    t("message.clientProject.deleteConfirm", { name: row.name }),
    t("message.common.tip"),
    {
      confirmButtonText: t("message.common.confirm"),
      cancelButtonText: t("message.common.cancel"),
      type: "warning",
    }
  )
    .then(async () => {
      try {
        const requestData = {
          id: row.id
        };
        const res = await projectDelete(requestData);
        if (res?.success) {
          ElMessage.success(t("message.clientProject.deleteSuccess"));
          handleSearch();
        } else {
          ElMessage.warning(res?.msg);
        }
      } catch (error) {
        ElMessage.error(error.message || t("message.clientProject.deleteFailed"));
      }
    })
    .catch(() => {
      ElMessage.info(t("message.clientProject.deleteCancelled"));
    });
};

// 打开监控弹窗
const handleViewDevice = (project) => {
  currentProjectId.value = project.id;
  currentProjectName.value = project.name;
  const realTimeText = t('message.clientProject.RealTimeMonitor');
  monitorDialogTitle.value = project.name ? `${project.name} - ${realTimeText}` : realTimeText;
  monitorDialogVisible.value = true;
};

const fetchDevices = async () => {
  loading.value = true;
  try {
    const params = {
      companyId: queryParams.companyId,
      businessId: queryParams.businessId,
      groupId:groupsOptionsId.value,      
      groupName: groupsOptionsName.value,      
      name: queryParams.name,
      pageNum: queryParams.pageNum,
      pageSize: queryParams.pageSize,
    };

    console.log('请求参数:', params);

    const res = await projectList(params);
    if (!res.success) {
      ElMessage.error(res?.msg);
      return;
    }
    deviceList.value = res.data.list;
    pagination.total = res.data.totalCount;
  } catch (error) {
    ElMessage.error(error.message || t("message.device.fetchError"));
  } finally {
    loading.value = false;
  }
};

// 新增项目
const handleAdd = () => {
  currentCompanyId.value = queryParams.companyId;
  currentProjectAddOrEdit.value = 1;
  currentProjectName.value = '';
  currentProjectId.value = 0
  proGroupsOptionsName.value = '';
  currentProjectTitle.value = t("message.clientProject.addProjectTitle")
  ProjectDeailAddDialogVisible.value = true;
};

const handleSearch = () => {
  queryParams.pageNum = 1;
  fetchDevices();
};

const resetSearch = () => {
  queryParams.name = "";
  queryParams.pageNum = 1;
  handleSearch();
};

const handlePageChange = (val) => {
  queryParams.pageNum = val;
  fetchDevices();
};

const handleSizeChange = (val) => {
  queryParams.pageSize = val;
  fetchDevices();
};

const loadCompanyTree = async () => {
  try {
    const res = await getCompanyTree(queryCompanyTreeParams);
    companyTreeData.value = res.data.list;

    if (companyTreeData.value.length > 0 && !queryParams.companyId) {
      queryParams.companyId = companyTreeData.value[0].id;
    }
  } catch (error) {
    ElMessage.error(t("message.device.loadCompanyFailed"));
  }
};

// 监控 businessId 变化
watch(
  () => businessId.value,
  async (newBusinessId, oldBusinessId) => {
    console.log('businessId 发生变化:', oldBusinessId, '->', newBusinessId);
    if (newBusinessId) {

      queryParams.businessId = newBusinessId;
      queryParams.pageNum = 1;
      await LoadGroupLabel();
      await handleSearch();
    }
  },
  { immediate: true }
);

// 路由变化时刷新权限
watch(
  () => route.fullPath,
  () => {
    refresh()
  }
)

// 窗口尺寸变化时重新检测滚动按钮
const handleResize = () => {
  checkScrollButtons()
}

// 初始化
onMounted(async () => {
  refresh();
  if (businessId.value) {
    queryParams.businessId = businessId.value;
  }
  await loadCompanyTree();
  await LoadGroupLabel();
  await handleSearch();

  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
/* ============ 外层：固定高度，不滚动 ============ */
.project-management-container {
  padding: 0px;
  height: 100%;
  overflow: hidden;   /* ★ 外层不滚动 */
  box-sizing: border-box;
}

.project-inner {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* el-card 占满高度 */
.box-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ★ 用 :deep 控制 el-card 内部布局 */
:deep(.box-card) {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* el-card 的 header 固定 */
:deep(.box-card .el-card__header) {
  flex-shrink: 0;
}

/* ★ el-card 的 body 用 flex 纵向布局，占满剩余高度 */
:deep(.box-card .el-card__body) {
  flex: 1;
  min-height: 0;            /* 关键：允许收缩 */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 16px;
  box-sizing: border-box;
}

/* 我给 body 加的 class（通过 body-class="card-body"） */
:deep(.box-card .card-body) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 16px;
  box-sizing: border-box;
}

/* 表格占满并滚动 */
:deep(.box-card .el-table) {
  flex: 1;
  min-height: 0;
}

/* ============ 大图标区：自己滚动 ============ */
.grid-view {
  flex: 1;                 /* ★ 占满剩余高度 */
  min-height: 0;           /* ★ 允许收缩，触发 overflow */
  overflow-y: auto;        /* ★ 纵向滚动 */
  overflow-x: hidden;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 4px;
  position: relative;
  align-content: flex-start;

  /* ★ 关键：始终为滚动条预留空间 */
  /*scrollbar-gutter: stable;*/
}

/* 滚动条美化 */
.grid-view::-webkit-scrollbar {
  width: 8px;
}

.grid-view::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.grid-view::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

.grid-view::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

/* ============ 底部工具栏：固定，不滚动 ============ */
.bottom-toolbar {
  flex-shrink: 0;          /* ★ 固定不收缩 */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #ebeef5;
}

/* 单选组容器 */
.radio-group-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

/* 滚动按钮 */
.scroll-btn {
  flex-shrink: 0;
}

/* 单选组滚动容器 */
.radio-group-container {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  scrollbar-width: thin;
  -ms-overflow-style: none;
}

.radio-group-container::-webkit-scrollbar {
  height: 4px;
}
.radio-group-container::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 2px;
}

/* 单选组 */
.business-radio-group {
  display: inline-flex;
  flex-wrap: nowrap;
}

.business-radio-group .el-radio {
  flex-shrink: 0;
  margin-right: 12px;
}

/* 右侧分页 */
.pagination {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
}

/* ============ 卡片头 ============ */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1px;
}

.page-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.search-form {
  margin-top: 0px;
  height: 5px;
}

.search-form :deep(.el-form-item) {
  margin-right: 15px;
  margin-bottom: 10;
}

/* ============ 大图标项 ============ */
.grid-item {
  position: relative;
  width: 130px;
  height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;
  border: 1px solid transparent;
}

.grid-item:hover {
  background-color: #ecf5ff;
  border-color: #b3d8ff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.grid-item-icon {
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border-radius: 8px;
  background: #f5f7fa;
  transition: background-color 0.2s;
}

.grid-item:hover .grid-item-icon {
  background: #d9ecff;
}

.grid-item-name {
  font-size: 13px;
  color: #303133;
  text-align: center;
  line-height: 1.4;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
}

/* 悬浮操作 */
.grid-item-actions {
  position: absolute;
  bottom: 6px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}

.grid-item:hover .grid-item-actions {
  opacity: 1;
  pointer-events: auto;
}

.grid-item-actions .el-button {
  width: 26px;
  height: 26px;
  padding: 0;
}

/* 弹窗样式 */
:deep(.custom-dialog .el-dialog__body) {
  padding: 0;
  height: calc(100% - 60px);
  overflow: hidden;
}

.dialog-content {
  height: 100%;
  padding: 0px;
  overflow: hidden;
  box-sizing: border-box;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .project-management-container {
    padding: 0px;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .search-form :deep(.el-form-item) {
    margin-right: 0;
    margin-bottom: 10px;
    width: 100%;
  }

  .search-form :deep(.el-input) {
    width: 100%;
  }

  .grid-view {
    gap: 10px;
    padding: 10px 0;
  }

  .grid-item {
    width: calc(50% - 5px);
    height: auto;
  }

  .bottom-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination {
    justify-content: center;
  }
}
</style>